/**
 * FAZ Cookie Manager
 * https://wordpress.org/plugins/faz-cookie-manager/
 *
 * Cookie name: fazcookie-consent
 * Cookie format (URL-encoded): "key:value,key:value,..."
 *   - Reserved keys: consentid, action, rev
 *   - Category keys (canonical): necessary, functional, analytics, performance, marketing
 *   - Legacy alias: advertisement (treated as marketing, matches FAZ's own tcf-cmp.min.js)
 *   - Service keys: svc.<id>
 *   - Value "yes" means granted, anything else means denied
 *
 * Revision check: if the configured _fazConfig._consentRevision is > 1 and the
 * cookie's "rev" is below it, the consent is considered stale and treated as missing
 * (the visitor must re-consent).
 *
 * Event: fazcookie_consent_update is dispatched on document whenever the
 * visitor changes consent. detail = { accepted: [slugs], rejected: [slugs], action }.
 */

export const fazCookieManager = {

	/**
	 * Read the FAZ Cookie Manager consent cookie and map it to PMW categories.
	 *
	 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}|null}
	 */
	getConsent: () => {

		const parsed = parseFazConsentCookie()

		if (!parsed) return null

		pmw.console.log("FAZ Cookie Manager CMP consent detected")

		return mapFazCategoriesToPmw(parsed)
	},

	/**
	 * Listen for FAZ consent updates and forward them to PMW.
	 */
	loadEventListeners: () => {
		document.addEventListener("fazcookie_consent_update", fazConsentUpdate)
	},
}

/**
 * Forward a FAZ consent update to PMW.
 *
 * @param {CustomEvent} e
 */
const fazConsentUpdate = (e) => {

	const detail = e && e.detail ? e.detail : null

	// Skip the initial sync the banner fires on page load. PMW already read the
	// cookie via getConsent() at that point.
	if (detail && detail.action === "init") return

	// Re-read the cookie so we use the canonical, persisted state rather than
	// trying to interpret the event payload (which uses category slugs only).
	const parsed = parseFazConsentCookie()

	if (!parsed) return

	pmw.consent.api.updateSelectively({...mapFazCategoriesToPmw(parsed), source: "cmp"})
}

/**
 * Parse the fazcookie-consent cookie into a flat object.
 *
 * @returns {Object|null}
 */
const parseFazConsentCookie = () => {

	const raw = pmw.getCookie("fazcookie-consent")

	if (!raw) return null

	let decoded = raw

	try {
		decoded = decodeURIComponent(raw)
	} catch (err) {
		// Cookie was stored without encoding; use the raw value.
	}

	const out = {}

	decoded.split(",").forEach((pair) => {
		const idx = pair.indexOf(":")
		if (idx === -1) return
		const key   = pair.substring(0, idx).trim()
		const value = pair.substring(idx + 1).trim()
		if (key) out[key] = value
	})

	// Revision invalidation: if the site requires a newer revision than the
	// one recorded in the cookie, treat the cookie as absent.
	const configuredRevision = (typeof window !== "undefined" && window._fazConfig && parseInt(window._fazConfig._consentRevision, 10)) || 0
	const cookieRevision     = parseInt(out.rev, 10) || 0

	if (configuredRevision > 1 && cookieRevision < configuredRevision) return null

	return out
}

/**
 * Map FAZ category values to PMW's four buckets.
 *
 * @param {Object} parsed
 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}}
 */
const mapFazCategoriesToPmw = (parsed) => {

	const isYes = (key) => parsed[key] === "yes"

	return {
		statistics : isYes("analytics") || isYes("performance"),
		marketing  : isYes("marketing") || isYes("advertisement"),
		preferences: isYes("functional"),
		necessary  : true,
	}
}
